'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Box, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { CorrectionIcon, DeclarationIcon, DeleteIcon, LockOpenIcon, MenuUSerSS, UnLockOpenIcon, ChangeStateIcon, EyesIcon, BlockUserIcon, FollowIcon, UnfollowIcon } from '@/public/icon';
import DrawerCustom from '@/app/components/MUI/CustomDrawer';
import { ObjectType, PostTypeConfig } from '@/app/constants/typedef';
import { useUser } from '@/app/hooks/useUser';
import BaseModal from '@/app/base-ui/BaseModalUI';
import CenteredBox from '@/app/base-ui/CenterBox';
import ProductStatusModal from '../ProductStatusModal';
import { useTranslation } from '@/app/hooks/useTranslation';
import { useSnackbar } from '@/app/contexts/snack-bar-provider';
import { useGlobalPostState, PostUpdateType } from '@/app/contexts/global-post-state-context';
import { canEnterRoom, useRoomEntryAllowed, ROOM_UPDATE_NOTICE } from '@/app/components/MyRoom/appCompat';
import { rememberReturnPath } from '@/app/components/MyRoom/returnPath';
import { withMr } from '@/app/components/MyRoom/mrEnv';
import { myChannelService } from '@/app/service/myChannelService';
import { actionFeedServices } from '@/app/service/actionFeedService';
import { logger } from '@/app/lib/logger';
import { useEffect } from 'react';
import utilsService from '@/app/helpers/utilsService';
import { useFollowUser } from '@/app/hooks/useFollowUser';
import LevelUpModal from '@/app/components/Common/LevelUpModal';

interface UserActionsPermissionsProps {
    onEdit?: () => void;
    onDelete?: () => void;
    onReport?: () => void;
    onHiddenPost?: () => void;
    onTogglePrivacy?: () => void;
    onBlockUser?: () => void;
    ownerId?: string;
    isPublic: boolean;
    type?: PostTypeConfig;
    productStatus?: 'selling' | 'reserved' | 'sold' | null;
    onProductStatusChange?: (status: 'selling' | 'reserved' | 'sold') => void;
    onFollow?: () => void;
    isFollowing?: boolean;
    postId?: string;
}

const UserActionsPermissions: React.FC<UserActionsPermissionsProps> = ({ onEdit, onDelete, onReport, onHiddenPost, onTogglePrivacy, onBlockUser, ownerId, isPublic, type, productStatus, onProductStatusChange, onFollow, isFollowing, postId }) => {
    const { t } = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [openPopupConfirm, setIsOpenPopupConfirm] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const { user } = useUser();
    const { addSnackbar } = useSnackbar();
    const router = useRouter();
    const isOwner = user?.id === ownerId;
    const roomEntryAllowed = useRoomEntryAllowed();
    const globalPostState = useGlobalPostState();

    const {
        isFollowing: localIsFollowing,
        handleFollowToggle,
        bonusPoints,
        showBonusModal,
        setShowBonusModal,
        levelUpData,
        showLevelUpModal,
        setShowLevelUpModal
    } = useFollowUser({
        userId: ownerId || '',
        initialIsFollowing: isFollowing,
        onSuccess: onFollow
    });

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setDrawerOpen(true);
    };
    const actionLabels: { [key: string]: ObjectType } = {
        change_status: { key: 'change_status', title: t('change_status', '상태 변경'), icon: <ChangeStateIcon />, action: 'changeStatus', show: isOwner && type === PostTypeConfig.Products },
        declaration: { key: 'declaration', title: t('report', '신고'), icon: <DeclarationIcon />, action: 'report', show: !isOwner },
        correction: { key: 'correction', title: t('edit', '수정'), icon: <CorrectionIcon />, action: 'edit', show: isOwner },
        delete: { key: 'delete', title: t('delete', '삭제'), icon: <DeleteIcon />, action: 'delete', show: isOwner },
        go_private: { key: 'go_private', title: isPublic ? t('make_private', '비공개로 전환') : t('make_public', '공개로 전환'), icon: isPublic ? <LockOpenIcon /> : <UnLockOpenIcon />, action: 'togglePrivacy', show: isOwner },
        hidden_post: { key: 'hidden_post', title: t('hide_post', '게시물 숨기기'), icon: <EyesIcon />, action: 'hiddenPosts', show: !isOwner},
        block_user: { key: 'block_user', title: t('block_user', '차단하기'), icon: <BlockUserIcon />, action: 'blockUser', show: !isOwner },
        follow: { 
            key: 'follow', 
            title: localIsFollowing ? t('unfollow', '언팔로우 하기') : t('follow', '팔로우 하기'), 
            icon: localIsFollowing ? <UnfollowIcon /> : <FollowIcon />, 
            action: 'follow', 
            show: !isOwner 
        },
        // 시안(Figma 4278:31759) 기준 맨 아래. 아이콘은 홈 퀵메뉴 "3D전시관" 과 같은 계열이라 재사용한다.
        myroom: {
            key: 'myroom',
            title: t('go_myroom', '마이룸 이동하기'),
            icon: <Image src="/icon/myroom3d.svg" alt="" width={24} height={24} />,
            action: 'goMyRoom',
            // 방이 없어 보이는 작성자에게도 노출한다(위 프로필과 같은 이유).
            // 방이 없으면 마이룸이 room_unavailable 로 답하고 안내 화면이 뜬다.
            // 단 마이룸을 쓸 수 없는 앱(구버전)에서는 항목 자체를 빼둔다.
            show: !isOwner && roomEntryAllowed,
        },
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    const openPopupConfirmDeleted = () => {
        setIsOpenPopupConfirm(true);
    };

    const handleClickAction = (key: string) => {
        switch (key) {
            case 'changeStatus':
                handleDrawerClose();
                setTimeout(() => {
                    setStatusModalOpen(true);
                }, 300);
                break;
            case 'edit':
                onEdit?.();
                handleDrawerClose();
                break;
            case 'delete':
                openPopupConfirmDeleted();
                handleDrawerClose();
                break;
            case 'blockUser':
                handleBlockUserInternal();
                handleDrawerClose();
                break;
            case 'report':
                handleReportInternal();
                handleDrawerClose();
                break;
            case 'togglePrivacy':
                onTogglePrivacy?.();
                handleDrawerClose();
                break;
            case 'hiddenPosts':
                onHiddenPost?.();
                handleDrawerClose();
                break;
            case 'blockUser':
                onBlockUser?.();
                handleDrawerClose();
                break;
            case 'follow':
                handleDrawerClose();
                handleFollowToggle();
                break;
            case 'goMyRoom':
                handleDrawerClose();
                // 구버전 iOS 앱은 방 iframe 이 앱 밖으로 튕긴다(appCompat 참고).
                if (!canEnterRoom()) {
                    addSnackbar({
                        message: `${ROOM_UPDATE_NOTICE.title} ${ROOM_UPDATE_NOTICE.message}`,
                        severity: 'warning',
                        type: 'toast',
                    });
                    break;
                }
                // 방에서 나올 때 이 화면으로 돌아온다. target 은 조이쌤 user id 다(실측 확인).
                rememberReturnPath();
                router.push(withMr(`/myroom?target=${encodeURIComponent(ownerId || '')}`));
                break;
            default:
                break;
        }
    };

    const handleBlockUserInternal = async () => {
        try {
            if (!ownerId) return;

            await myChannelService.blockUser(ownerId);
            addSnackbar({
                message: t('user_blocked_success') || '차단이 완료되었습니다. 이제 이 사용자의 게시글을 더 이상 보지 않게 됩니다.',
                severity: 'success',
                type: 'toast',
            });

            // Dispatch to global post state to refresh all pages
            globalPostState.blockUser(ownerId, 'UserActionsPermissions');
            
            // Call onBlockUser callback if provided (for parent-specific logic)
            onBlockUser?.();
        } catch (error) {
            const errorMessage = utilsService.getErrorMessage(error);
            addSnackbar({
                message: errorMessage || t('user_block_error') || '사용자 차단 중 오류가 발생했습니다.',
                severity: 'error',
                type: 'toast',
            });
            logger.error(error);
        }
    };

    const handleReportInternal = async () => {
        try {
            if (!postId) return;
            
            // Map type if needed (e.g. Products might use 'posts' for reporting depending on API)
            const reportType = type === PostTypeConfig.Products ? 'posts' : 'posts'; // Standardizing for now as per common projects pattern

            await actionFeedServices.reportPost({ type: reportType as any, id: postId });
            addSnackbar({
                message: t('post_reported_success') || '해당 게시글을 성공적으로 신고하였습니다.',
                severity: 'info',
                type: 'toast',
            });
            
            // Call onReport callback if provided
            onReport?.();
        } catch (error) {
            const errorMessage = utilsService.getErrorMessage(error);
            addSnackbar({
                message: errorMessage || 'Some thing went wrong',
                severity: 'error',
                type: 'toast',
            });
            logger.error(error);
        }
    };

    return (
        <>
            <IconButton
                // aria-label="settings"
                onClick={handleClick}
                // sx={{ marginTop: -2 }}
            >
                <MenuUSerSS />
            </IconButton>
            <DrawerCustom
                open={drawerOpen}
                onClose={handleDrawerClose}
                title={t('post_type', '게시물')}
            >
                <CenteredBox sx={{ flexDirection: 'column', mt: 5, mb: 7.5, justifyContent: 'start', gap: '16px' }}>
                    {Object.keys(actionLabels).map((item, index) => {
                        return (
                            actionLabels[item].show && (
                                <CenteredBox
                                    key={item}
                                    sx={{ width: '100%' }}
                                >
                                    <List
                                        key={item}
                                        sx={{ padding: 0, width: '100%' }}
                                    >
                                        <ListItem
                                            disablePadding
                                            sx={{ p: 0, cursor: 'pointer' }}
                                            onClick={() => handleClickAction(actionLabels[item].action)}
                                        >
                                            <ListItemButton 
                                                sx={{
                                                    py: 0, 
                                                    minHeight: '24px',  
                                                    px: 5, 
                                                    gap: 3, 
                                                    width: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <ListItemIcon 
                                                    sx={{ 
                                                        minWidth: 'fit-content',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    {actionLabels[item].icon}
                                                </ListItemIcon>
                                                <ListItemText
                                                    sx={{ 
                                                        margin: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        '& .MuiListItemText-primary': {
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        }
                                                    }}
                                                    primary={
                                                        <Typography
                                                            variant="h2_14Medium"
                                                            lineHeight={'100%'}
                                                            sx={{ display: 'flex', alignItems: 'center' }}
                                                        >
                                                            {actionLabels[item].title}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    </List>
                                </CenteredBox>
                            )
                        );
                    })}
                </CenteredBox>
            </DrawerCustom>
            {openPopupConfirm && (
                <BaseModal
                    lineHeight="normal"
                    open={openPopupConfirm}
                    onClose={() => setIsOpenPopupConfirm(false)}
                    title={t('post_delete_confirm', '삭제한 게시물은 복구할 수 없습니다.<br />정말 삭제하시겠습니까?')}
                    description={''}
                    showAccept={true}
                    showCancel={true}
                    onCancel={() => setIsOpenPopupConfirm(false)}
                    onAccept={() => {
                        onDelete?.();
                        setIsOpenPopupConfirm(false);
                    }}
                    acceptText={t('delete', '삭제')}
                    cancelText={t('cancel', '취소')}
                />
            )}
            {statusModalOpen && (
                <ProductStatusModal
                    open={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    currentStatus={productStatus || 'selling'}
                    onStatusChange={(status) => {
                        onProductStatusChange?.(status);
                    }}
                />
            )}
            {/* Follow Bonus Points Modal */}
            {showBonusModal && (
                <BaseModal
                    open={showBonusModal}
                    onClose={() => {
                        setShowBonusModal(false);
                        if (levelUpData) {
                            setTimeout(() => {
                                setShowLevelUpModal(true);
                            }, 500);
                        }
                    }}
                    onCancel={() => {
                        setShowBonusModal(false);
                        if (levelUpData) {
                            setTimeout(() => {
                                setShowLevelUpModal(true);
                            }, 500);
                        }
                    }}
                    title={`${bonusPoints.toLocaleString()} ${t('bonus_points_earned', '포인트를 획득했습니다!')}`}
                    description={''}
                    showCancel={false}
                    showAccept={true}
                    onAccept={() => {
                        setShowBonusModal(false);
                        if (levelUpData) {
                            setTimeout(() => {
                                setShowLevelUpModal(true);
                            }, 500);
                        }
                    }}
                    acceptText={t('attendance_confirm', '확인')}
                />
            )}
            {/* Level Up Modal */}
            <LevelUpModal
                open={showLevelUpModal}
                onClose={() => setShowLevelUpModal(false)}
                levelUpData={levelUpData}
            />
        </>
    );
};

export default UserActionsPermissions;
